import { useEffect, useState, useCallback } from "react"

import { useSelector } from "react-redux"

import PeopleAvatar from "@components/PeopleAvatar"
import { selectActiveTeeupId } from "@selectors/activeTeeup"
import { addTeeupInvitees } from "@actions/teeupActions"
import { fetchInviteSuggestions } from "@utils/devBackendShim"

import Block from "../Block"

import "./index.scss"

// A7 (invite-to-an-existing-TeeUp suggestions, QJ 2026-07-07): "Suggested people" detail group
// on the People card. Read-only ranking from GET /coordination-objects/{id}/invite-suggestions
// (api/routers/invite_suggestions.py) -- deterministic, no LLM, every candidate carries a `why`
// evidence list. Only ever a SUPPLEMENT to the existing Invite flow (SubTitleContent's Invite
// pill), never a replacement: this group is simply omitted (renders nothing) when there are no
// candidates, an unresolved TeeUp, or the fetch fails -- honest-empty, same convention as the
// Stance tab (useProfilePageStance.js).
//
// ONE-TAP ADD reuses the EXISTING add-invitee path verbatim (addTeeupInvitees, the same action
// ParticipantListActions/hooks/useActions.js's "Resend Invite" one-tap action calls) -- a
// resolved candidate posts `{id: user_id}`; an unresolved candidate (no CooWe account yet, only
// a contact mechanism on file) posts the manual-invite shape `{email|phone: value}` so it lands
// as a pending_invites row via the backend's existing channel+value branch (participants.py
// add_participant), never a second invite mechanism.
const WHY_LABELS = {
  frequent_co_attendee: "usually in these meetings",
  shared_work_unit: "on this team",
  purpose_match: "meets for this kind of thing",
  relationship_score: "strong relationship",
}

const topWhyChip = why => {
  if (!why || !why.length) return null
  const top = [...why].sort((a, b) => (b.weight * b.raw || 0) - (a.weight * a.raw || 0))[0]
  return WHY_LABELS[top.signal] || null
}

const SuggestedPeople = () => {
  const teeupId = useSelector(selectActiveTeeupId)
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(true)
  const [addingKey, setAddingKey] = useState(null)
  const [addedKeys, setAddedKeys] = useState(() => new Set())

  const load = useCallback(async () => {
    if (!teeupId) return
    setLoading(true)
    const result = await fetchInviteSuggestions(teeupId)
    setCandidates(result.ok ? result.data?.candidates || [] : [])
    setLoading(false)
  }, [teeupId])

  useEffect(() => {
    setCandidates([])
    setAddedKeys(new Set())
    if (teeupId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teeupId])

  const handleAdd = async candidate => {
    if (!teeupId || addingKey) return
    setAddingKey(candidate.counterpart_key)
    const invitee = candidate.user_id
      ? { id: candidate.user_id }
      : { [candidate.channel === "phone" ? "phone" : "email"]: candidate.value }
    try {
      await addTeeupInvitees(teeupId, [invitee])
      setAddedKeys(prev => new Set(prev).add(candidate.counterpart_key))
    } catch (e) {
      console.error('addTeeupInvitees failed', e)
    } finally {
      setAddingKey(null)
    }
  }

  const visible = candidates.filter(c => !addedKeys.has(c.counterpart_key))

  if (loading || visible.length === 0) return null

  return (
    <Block
      className="people-suggested-group"
      data-testid="teeup-detail-suggested-people">
      <h4>Suggested people</h4>
      <div className="people-suggested-list">
        {visible.slice(0, 5).map(candidate => {
          const label = candidate.display_name || candidate.value || candidate.counterpart_key
          const whyChip = topWhyChip(candidate.why)
          const isAdding = addingKey === candidate.counterpart_key
          return (
            <div className="people-suggested-row" key={candidate.counterpart_key}>
              <PeopleAvatar
                avatar={candidate.avatar_url}
                name={label}
                className="people-suggested-avatar"
                fallbackClassName="people-suggested-avatar people-suggested-avatar-fallback"
              />
              <div className="people-suggested-info">
                <span className="people-suggested-name">{label}</span>
                {whyChip && <span className="people-suggested-why">{whyChip}</span>}
              </div>
              <button
                type="button"
                className="people-suggested-add"
                disabled={isAdding}
                data-testid="teeup-detail-suggested-add"
                onClick={() => handleAdd(candidate)}>
                {isAdding ? "Adding..." : "Add"}
              </button>
            </div>
          )
        })}
      </div>
    </Block>
  )
}

export default SuggestedPeople
